/**
 * Dioptric values.
 *
 * Appendix D `MR-02` and Appendix H: stored as fixed-point decimal in 0.25 steps, never a float.
 * Here that is an integer count of quarter-dioptres, so no arithmetic ever touches a binary
 * fraction. The database column is `numeric(5,2)` with a check constraint on the 0.25 multiple.
 */
const QUARTERS_PER_DIOPTRE = 4;
export class DioptreError extends Error {
}
/**
 * Build a Dioptre from a decimal value.
 *
 * Rejects anything that is not an exact 0.25 multiple rather than rounding to one, because a silently
 * rounded sphere is the most expensive typo in the product (`DESIGN-TOKENS` §9 `RX-03`).
 */
export function dioptre(value) {
    if (!Number.isFinite(value)) {
        throw new DioptreError(`Dioptric value must be finite, got ${value}`);
    }
    const quarters = value * QUARTERS_PER_DIOPTRE;
    const rounded = Math.round(quarters);
    if (Math.abs(quarters - rounded) > Number.EPSILON * 8) {
        throw new DioptreError(`Dioptric values step by 0.25, got ${value}`);
    }
    return rounded;
}
export function toNumber(d) {
    return d / QUARTERS_PER_DIOPTRE;
}
/**
 * Render for display and for print.
 *
 * `RX-03`: the sign is always rendered, including `+`. Two decimals always, because a column of
 * powers aligns on the decimal (`TY-04`).
 */
export function formatDioptre(d) {
    const value = toNumber(d);
    const sign = value < 0 ? '-' : '+';
    return `${sign}${Math.abs(value).toFixed(2)}`;
}
/** Appendix I: sphere −30.00 to +30.00, blocking outside. */
export const SPHERE_RANGE = { min: -30, max: 30 };
/** Appendix I: cylinder −10.00 to +10.00, blocking outside. */
export const CYLINDER_RANGE = { min: -10, max: 10 };
export function isWithin(d, range) {
    const value = toNumber(d);
    return value >= range.min && value <= range.max;
}
//# sourceMappingURL=dioptre.js.map